// Отложенный прекэш lazy-ассетов: сигнал service worker'у «приложение ожило».
//
// Service worker прекэширует на install только стартовый набор (см. шапку
// `public/sw.js`). Остальные сотни content-hashed чанков он добирает по этому
// сообщению — не раньше, чем первичная синхронизация PowerSync завершилась,
// либо истёк потолок ожидания. Раньше install тянул весь `dist/assets`
// параллельно с первым рендером, и открытие сразу после деплоя показывало
// немой скелетон десятки секунд.

import { getPostHog, isPostHogEnabled } from "@/lib/observability/posthog-client";
import { peekPowerSync } from "@/lib/powersync/db-instance";

const PRECACHE_REST_MESSAGE = "sw:precache-rest";
/** Потолок ожидания первичного синка: дальше кэш важнее экономии полосы. */
const FALLBACK_DELAY_MS = 30_000;
const POLL_INTERVAL_MS = 1_000;

interface PrecacheDoneMessage {
	type: "sw:precache-done";
	scope: "critical" | "deferred";
	ms: number;
	count: number;
}

function isPrecacheDoneMessage(data: unknown): data is PrecacheDoneMessage {
	return (
		typeof data === "object" &&
		data !== null &&
		(data as { type?: unknown }).type === "sw:precache-done"
	);
}

function reportPrecacheDone(message: PrecacheDoneMessage): void {
	if (!isPostHogEnabled()) return;
	try {
		getPostHog()?.capture("sw_precache_done", {
			sw_precache_scope: message.scope,
			[`sw_precache_${message.scope}_ms`]: message.ms,
			sw_precache_count: message.count,
		});
	} catch {
		// no-op
	}
}

function hasSynced(): boolean {
	try {
		return peekPowerSync()?.currentStatus?.hasSynced === true;
	} catch {
		return false;
	}
}

async function requestDeferredPrecache(): Promise<void> {
	try {
		const registration = await navigator.serviceWorker.ready;
		registration.active?.postMessage({ type: PRECACHE_REST_MESSAGE });
	} catch {
		// SW мог быть не активирован — прекэш это оптимизация, не условие работы.
	}
}

/**
 * Планирует отложенный прекэш: как только PowerSync отчитался о завершённом
 * первичном синке, либо через {@link FALLBACK_DELAY_MS}. Вызывать один раз
 * после регистрации service worker'а.
 */
export function scheduleDeferredPrecache(): void {
	if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

	navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
		if (isPrecacheDoneMessage(event.data)) reportPrecacheDone(event.data);
	});

	let done = false;
	const fire = (): void => {
		if (done) return;
		done = true;
		clearInterval(poll);
		clearTimeout(fallback);
		void requestDeferredPrecache();
	};

	// Поллинг, а не подписка на статус: модуль живёт вне React, а инстанс БД к
	// этому моменту может быть ещё не создан (`peek` вернёт null до логина).
	const poll = setInterval(() => {
		if (hasSynced()) fire();
	}, POLL_INTERVAL_MS);
	const fallback = setTimeout(fire, FALLBACK_DELAY_MS);
}
